import React, {useCallback, useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import MemberRegisterInformationDTO from '../../../DTO/MemberRegisterInformationDTO'
import {MemberInfoForConfirmPage} from '../../../Props/MemberRegisterPageLocationStates'
import {MailMagazine} from '../../../DTO/MailMagazineDTO'
import {Signboard} from '../../../DTO/SignboardDTO'
import {Optional} from '../../../../Common/TypeHelper'
import {RegisterOrganizationContentBlock} from '../../Components/RegisterOrganizationContentBlocks/RegisterOrganizationContentBlock'
import {BasicMemberInfoConfirmContents} from '../../Components/ConfirmContentBlocks/BasicMemberInfoConfirmContents'
import {ContractInfoWithContractDate, ContractInputInfo, ContractMethod, ContractTerm, ConvenienceStoreInfo, CreditCardInfo, MemberRegisterContractInfo, MemberRegisterConvenienceStoreContractInfo, MemberRegisterFreeBucketContractInfo} from '../../../Props/ContractInputInfo'
import {PAYMENT} from '../../../../Common/Constants'
import {MBMemberError} from '../../../Common/MBMemberError'
import {TokenResponse, useGmo} from '../../Hooks/UseGmo'
import {PaymentMethod} from '../../../DTO/PaymentMethodDTO'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {ContentBlock} from '../../Molecules/BaseContentBlocks/ContentBlock'
import {ContentTitle} from '../../Molecules/BaseContentBlocks/ContentTitle'
import {ContentTopMessage} from '../../Molecules/BaseContentBlocks/ContentTopMessage'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {ContractTermConfirmContent} from '../../Components/PaymentsRelations/ConfirmContentBlocks/ContractTermConfirmContent'
import {ContractPaymentMethodConfirm} from '../../Components/PaymentsRelations/ConfirmContentBlocks/ContractPaymentMethodConfirm'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {Spacer} from '../../../../Common/Components/Spacer'
import {PreMemberRegisterInformationDTO} from '../../../DTO/PreMemberRegisterInformationDTO'
import {MBContentBodyDivider} from '../../Atoms/Dividers'
import {BucketRegisterResponseDataDTO, FreeBucketRegisterResponseDataDTO, isFreeBucketContract} from '../../../DTO/BucketRegisterResponseDataDTO'
import {CvsContractDTO, isCvsContract} from '../../../DTO/CvsContractDTO'
import {ContactAddressConfirmContents} from '../../Components/ConfirmContentBlocks/AddressConfirms/ContactAddressConfirmContents'
import {EnrollmentReasonConfirmContentBlock} from '../../Components/ConfirmContentBlocks/EnrollmentReasonConfirmContentBlock'
import {useTranslation} from 'react-i18next'
import {ExtendMetaData, ExtendMetaDataInputInfo} from '../../../DTO/ExtendMetaDataDTO'
import {ExtendMetaDataConfirmBlock} from './Components/ExtendMetaDataConfirmBlock'

type MembersSignupInputConfirmPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}
export type SignupInputConfirmPageLocationState = {
  memberInfo: MemberInfoForConfirmPage,
  signboard: Signboard,
  paymentMethodList: PaymentMethod[],
  contractInputInfo: ContractInputInfo
  extendMetaData: ExtendMetaData[]
  extendMetaDataInputInfo?: ExtendMetaDataInputInfo
}

export const MembersSignupInputConfirmPage: React.VFC<MembersSignupInputConfirmPageProps> =
  (props) => {
    const {t} = useTranslation('member')

    const scriptUrl: Optional<string> = process.env.REACT_APP_MEMBUCKET_ADMIN_APP_GMO_SCRIPT_URL
    const timeout: Optional<number> =
      parseInt(process.env.REACT_APP_MEMBUCKET_ADMIN_APP_GMO_SCRIPT_TIMEOUT ?? '5000', 10)

    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const [processing, setProcessing] = useState<boolean>(false)

    const [tokenResponse, setTokenResponse] = useState<TokenResponse>()

    if (!scriptUrl) {
      throw new MBMemberError('SystemError')
    }
    const [loading, error, gmo] = useGmo(scriptUrl)

    const location = useLocation<SignupInputConfirmPageLocationState>()
    const locationState: SignupInputConfirmPageLocationState = location.state

    const [gmoTimeoutErrorFlg, setGmoTimeoutErrorFlg] = useState(false)

    const throwMBMemberError = useMBMemberError()

    useEffect(() => {
      if (!locationState?.contractInputInfo) {
        return
      }

      if (gmo) {
        if (locationState.contractInputInfo.method !== 'credit_card') {
          return
        }
        if (!locationState.contractInputInfo.creditCard) {
          return
        }

        const shopCode = locationState.signboard.shopId
        if (!shopCode) {
          throw new MBMemberError('SystemError')
        }

        const {cardNumber, expireMonth, expireYear, holderName, securityCode} =
          locationState.contractInputInfo.creditCard.newCreditCardInfo

        gmo.init(shopCode)

        const timeoutId = setTimeout(() => {
          setGmoTimeoutErrorFlg(true)
        }, timeout)

        gmo.getToken({
          tokennumber: 2,
          cardno: cardNumber,
          expire: expireYear.slice(-2) + expireMonth,
          securitycode: securityCode,
          holdername: holderName,
        }, (result: TokenResponse) => {
          clearTimeout(timeoutId)
          setTokenResponse(result)
        })
      }
    }, [gmo])

    const goToMembersRegisteredPage = useCallback(async (): Promise<void> => {
      setProcessing(true)
      const {term, method, amount, creditCard, mobileCarrier, convenienceStore}
        = locationState.contractInputInfo

      if (method === 'credit_card' || method === 'convenience_store' || method === 'none') {
        await props.memberRepo.postCreateMember(new MemberRegisterInformationDTO(
          locationState.memberInfo.invitationId,
          locationState.memberInfo.familyName,
          locationState.memberInfo.firstName,
          locationState.memberInfo.familyNameKana,
          locationState.memberInfo.firstNameKana,
          locationState.memberInfo.gender,
          locationState.memberInfo.birthday,
          locationState.memberInfo.postCode,
          locationState.memberInfo.addressPrefectureCode,
          locationState.memberInfo.addressCity,
          locationState.memberInfo.addressStreet,
          locationState.memberInfo.addressBuilding,
          locationState.memberInfo.phoneMobile,
          locationState.memberInfo.phoneTel,
          locationState.memberInfo.password,
          locationState.memberInfo.agreedIdList,
          locationState.memberInfo.mailMagazineIdCheckedList,
          getRegisterContractInfo(method, term, creditCard, tokenResponse, convenienceStore),
          locationState.memberInfo.enrollmentReasonSelectedInfo,
          locationState.extendMetaDataInputInfo,
        )).then((resultCreateMember) => {
          switch (resultCreateMember.status) {
            case CommonResponseStatus.Created:
              setErrorMessages([])
              if (!resultCreateMember.data) {
                throw new MBMemberError('SystemError')
              }
              gotoMembersRegisteredPage(
                props, locationState, resultCreateMember.data, term, method, amount)
              break
            case CommonResponseStatus.BadRequest:
              if (resultCreateMember.errorMessageList !== null &&
                resultCreateMember.errorMessageList.length !== 0) {
                setProcessing(false)
                setErrorMessages(resultCreateMember.errorMessageList)
              }
              break
            case CommonResponseStatus.Conflict:
              throw new MBMemberError('Conflict')
            case CommonResponseStatus.InternalServerError:
              throw new MBMemberError('ServerError')
            case CommonResponseStatus.InvalidCreditCard:
              props.navigator.backToMembersSignupInputPageCauseCreditCardError(
                locationState.memberInfo,
                locationState.signboard,
                locationState.paymentMethodList,
                locationState.contractInputInfo,
                locationState.extendMetaData,
                locationState.extendMetaDataInputInfo,
              )
              break
            case CommonResponseStatus.PaymentSystemError:
              throw new MBMemberError('SystemError')
            default:
              throw new MBMemberError('ServerError')
          }
        }).catch((e) => {
          const errorMessage = e instanceof Error ? e.message : 'SystemError'
          throwMBMemberError(errorMessage)
        })
      }

      if (method === 'mobile_carrier') {
        await props.memberRepo.postCreatePreMember(
          new PreMemberRegisterInformationDTO(
            locationState.memberInfo.invitationId,
            locationState.memberInfo.familyName,
            locationState.memberInfo.firstName,
            locationState.memberInfo.familyNameKana,
            locationState.memberInfo.firstNameKana,
            locationState.memberInfo.gender,
            locationState.memberInfo.birthday,
            locationState.memberInfo.postCode,
            locationState.memberInfo.addressPrefectureCode,
            locationState.memberInfo.addressCity,
            locationState.memberInfo.addressStreet,
            locationState.memberInfo.addressBuilding,
            locationState.memberInfo.phoneMobile,
            locationState.memberInfo.phoneTel,
            locationState.memberInfo.password,
            locationState.memberInfo.agreedIdList,
            locationState.memberInfo.mailMagazineIdCheckedList,
            {
              contractTerm: term,
              paymentMethod: method,
              paymentMethodDetail: mobileCarrier.carrierKey,
            },
            locationState.memberInfo.enrollmentReasonSelectedInfo,
            locationState.extendMetaDataInputInfo,
          )).then((resultCreatePreMember) => {
          switch (resultCreatePreMember.status) {
            case CommonResponseStatus.Created:
              setErrorMessages([])
              if (!resultCreatePreMember.data) {
                throw new MBMemberError('SystemError')
              }

              props.navigator.goToMembersSignupMobileCarrierTransitionPage({
                ...resultCreatePreMember.data,
              })
              break
            case CommonResponseStatus.BadRequest:
              setProcessing(false)
              setErrorMessages(resultCreatePreMember.errorMessageList)
              break
            case CommonResponseStatus.Conflict:
              throw new MBMemberError('Conflict')
            case CommonResponseStatus.InternalServerError:
              throw new MBMemberError('ServerError')
            case CommonResponseStatus.PaymentSystemError:
              throw new MBMemberError('SystemError')
            default:
              throw new MBMemberError('ServerError')
          }
        }).catch((e) => {
          const errorMessage = e instanceof Error ? e.message : 'SystemError'
          throwMBMemberError(errorMessage)
        })

      }
    }, [
      props.memberRepo,
      locationState.memberInfo,
      locationState.contractInputInfo,
      locationState.extendMetaDataInputInfo,
      tokenResponse,
    ])

    if (isInvalidLocationState(locationState)) {
      throw new MBMemberError('InvalidOperation')
    }

    if (locationState.contractInputInfo.method === 'credit_card') {
      if (gmoTimeoutErrorFlg) {
        throw new MBMemberError('SystemError')
      }

      if (error) {
        throw new MBMemberError('SystemError')
      }
      if (loading) {
        return null
      }
      if (!gmo) {
        return null
      }

      if (!tokenResponse) {
        return null
      }
    }

    const {
      memberInfo, signboard, contractInputInfo,
      extendMetaData, extendMetaDataInputInfo,
    } = locationState
    const {
      mail, familyName, familyNameKana, firstName, firstNameKana,
      postCode, addressPrefectureCode, addressCity, addressStreet, addressBuilding,
      gender, birthday, phoneTel, phoneMobile, mailMagazineIdCheckedList,
      enrollmentReasonSelectedInfo,
    } = memberInfo
    const {term, amount, method} = contractInputInfo

    const registerButtonLabel = locationState.contractInputInfo.method === 'mobile_carrier' ?
      '決済に進む' : '確定・登録する'

    return (
      <MembersPageTemplate hideFooterNavi={true}>
        <PageTitle>{`${signboard.signupName} 新規登録情報確認`}</PageTitle>

        <StepGuide activity={'signup'} current={'confirm'}/>

        <Spacer mb={'16px'}>
          <PIMStack alignItems={'center'}>
            <RegisterOrganizationContentBlock
              signboard={signboard}
              title={t('新規登録情報確認.入会バケット情報.見出し')}
              paymentInformationList={[{
                isFreeBucket: method === 'none',
                termName: PAYMENT.TERMS[term].displayLabel,
                contractAmount: amount,
                order: PAYMENT.TERMS[term].order,
                term: term,
              }]}
            />
          </PIMStack>
        </Spacer>

        <Spacer mb={'16px'}>
          <PIMStack alignItems={'center'}>
            <BasicMemberInfoConfirmContents
              topMessage={
                ['登録はまだ完了していません。', '下記内容を確認してください。']
              }
              mail={mail}
              familyName={familyName}
              firstName={firstName}
              familyNameKana={familyNameKana}
              firstNameKana={firstNameKana}
              gender={gender}
              birthday={birthday}
            />
          </PIMStack>
        </Spacer>

        <ContentBlock data-testid={'block-confirms'}>
          <ContentTitle>{t('新規登録情報確認.連絡先情報.見出し')}</ContentTitle>
          <PIMStack spacing={'18px'}>
            <ContentTopMessage>
              登録はまだ完了していません。<br/>下記内容を確認してください。
            </ContentTopMessage>
            <MBContentBodyDivider/>
            <ContactAddressConfirmContents
              postCode={postCode}
              addressPrefectureCode={addressPrefectureCode}
              addressCity={addressCity}
              addressStreet={addressStreet}
              addressBuilding={addressBuilding}
              phoneMobile={phoneMobile}
              phoneTel={phoneTel}
            />
          </PIMStack>
        </ContentBlock>

        {(extendMetaData.length > 0 && extendMetaDataInputInfo !== undefined) &&
          <Spacer mb={'16px'}>
            <PIMStack alignItems={'center'}>
              <ExtendMetaDataConfirmBlock
                extendMetaData={extendMetaData}
                extendMetaDataInputInfo={extendMetaDataInputInfo}
              />
            </PIMStack>
          </Spacer>
        }

        <MailMagazineSelectedList
          mailMagazineIdCheckedList={mailMagazineIdCheckedList}
          mailMagazineList={signboard.mailMagazineList}
        />

        <EnrollmentReasonConfirmContentBlock
          enrollmentReasonInfoList={signboard.mainService.enrollmentReasonList}
          enrollmentReasonSelectedInfo={enrollmentReasonSelectedInfo}
        />

        {method !== 'none' && (<>
          <ContractTermConfirmContent title={'契約単位'} term={term} contractMethod={method}/>

          <ContractPaymentMethodConfirm
            title={'お支払い方法'}
            amount={amount}
            method={method}
            methodDetail={{
              paymentInputDetailWithMethod: contractInputInfo,
              myCreditCardInfoList: [],
              maskedCardNo: tokenResponse?.tokenObject.maskedCardNo,
            }}
          />
        </>)}

        <MessageForAction>
          上記の内容に間違いがなければ<br/>
          {`『${registerButtonLabel}』ボタンを押してください。`}
        </MessageForAction>

        <PIMStack alignItems={'center'}>
          <ErrorDisplay errors={errorMessages}/>
        </PIMStack>

        <PIMPageActions>
          <PIMSubmitButton
            testId={'confirm-register-button'}
            disabled={processing}
            onClick={(): Promise<void> => goToMembersRegisteredPage()}
          >
            {registerButtonLabel}
          </PIMSubmitButton>
          <PIMSecondaryButton onClick={(): void => {
            props.navigator.goBack()
          }}>
            登録情報入力に戻る
          </PIMSecondaryButton>
        </PIMPageActions>
      </MembersPageTemplate>
    )
  }

const MailMagazineSelectedList = (
  props:
    {
      mailMagazineIdCheckedList: number[],
      mailMagazineList: MailMagazine[]
    },
): JSX.Element => {
  if (props.mailMagazineList.length === 0) {
    return <></>
  }

  const selectedMailMagazineNames = props.mailMagazineList
    .filter((item) => props.mailMagazineIdCheckedList.includes(item.id))

  return <ContentBlock data-testid={'block-mail-magazine'}>
    <ContentTitle>メルマガ</ContentTitle>
    {
      props.mailMagazineIdCheckedList.length === 0
        ? <p className="">メルマガのご希望はありません</p>
        : <ul className="list-disc_01 fts15">
          {selectedMailMagazineNames.map(item => <li key={item.id}>{item.name}</li>)}
        </ul>
    }
  </ContentBlock>
}

const isInvalidLocationState = (
  locationState: SignupInputConfirmPageLocationState,
): boolean => {
  if (!locationState) {
    return true
  }
  if (!locationState?.memberInfo) {
    return true
  }
  if (!locationState?.signboard) {
    return true
  }
  if (!locationState?.contractInputInfo) {
    return true
  }

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addressBuilding, phoneMobile, phoneTel, enrollmentReasonSelectedInfo,
    ...rest
  } = locationState.memberInfo
  return Object.values(rest).some(value => value === undefined)
}

const getRegisterContractInfo = (
  method: ContractMethod,
  term: ContractTerm,
  creditCard?: CreditCardInfo,
  tokenResponse?: TokenResponse,
  convenienceStore?: ConvenienceStoreInfo,
): MemberRegisterContractInfo
  | MemberRegisterConvenienceStoreContractInfo
  | MemberRegisterFreeBucketContractInfo => {
  if (method === 'convenience_store') {
    return {
      contractTerm: term,
      paymentMethod: 'convenience_store',
      paymentMethodDetail: convenienceStore!.convenienceStoreKey,
    }
  } else if (method === 'none') {
    return {
      contractTerm: term,
      paymentMethod: 'none',
    }
  } else {
    return {
      contractTerm: term,
      paymentMethod: 'credit_card',
      creditCardExpireDate:
        `${creditCard?.newCreditCardInfo.expireMonth}/${creditCard?.newCreditCardInfo.expireYear}`,
      tokenList: tokenResponse?.tokenObject.token,
    }
  }
}

const gotoMembersRegisteredPage = (
  props: MembersSignupInputConfirmPageProps,
  locationState: SignupInputConfirmPageLocationState,
  data: BucketRegisterResponseDataDTO | CvsContractDTO | FreeBucketRegisterResponseDataDTO,
  term: string,
  method: string,
  amount: number,
): void => {
  if (isCvsContract(data)) {
    props.navigator.goToMembersRegisteredPageForConvenienceStore(
      locationState.memberInfo,
      locationState.signboard,
      data,
      {
        term: term,
        method: method,
        amount: amount,
        contractDate: {
          contractDate: data.contractDate,
          contractEndDate: data.contractEndDate,
        },
      } as ContractInfoWithContractDate,
    )
  } else if (isFreeBucketContract(data)) {
    props.navigator.goToMembersRegisteredPageForFreeBucket(
      locationState.memberInfo,
      locationState.signboard,
      data.bucketMemberIdList,
    )
  } else {
    props.navigator.goToMembersRegisteredPageForCreditCard(
      locationState.memberInfo,
      locationState.signboard,
      data.bucketMemberIdList, {
        term: term,
        method: method,
        amount: amount,
        contractDate: {
          contractDate: data.contractDate,
          contractEndDate: data.contractEndDate,
        },
      } as ContractInfoWithContractDate,
    )
  }
}
